import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Grid,
  Cards,
  Badge,
  Modal,
  Form,
  FormField,
  Select,
  Input,
  Alert,
  StatusIndicator,
  Multiselect
} from '@cloudscape-design/components'
import { apiService } from '../services/api'
import { useRole } from '../contexts/RoleContext'
import { fetchAuthSession } from 'aws-amplify/auth'

interface LocationList {
  id: string
  userId: string
  username: string
  dataProduct: string
  locationCount: number
  maxLocations: number
  lastUpdated: string
  referenceLocation?: { latitude: string; longitude: string }
  locations: Array<{
    locationId: string
    name: string
    order: number
    coordinates?: { latitude: number; longitude: number }
  }>
}

const LocationLists: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { isAdmin, isProduction, isLoading: roleLoading } = useRole()
  const filterUserId = searchParams.get('userId')
  
  const [locationLists, setLocationLists] = useState<LocationList[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletingList, setDeletingList] = useState<LocationList | null>(null)
  const [devices, setDevices] = useState<any[]>([])
  const [editingList, setEditingList] = useState<LocationList | null>(null)
  const [selectedLocations, setSelectedLocations] = useState<Array<{ label: string; value: string }>>([])
  const [availableLocations, setAvailableLocations] = useState<Array<{ label: string; value: string }>>([])
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [newList, setNewList] = useState({
    dataProduct: ''
  })
  const [currentUserId, setCurrentUserId] = useState<string>('')
  const [currentUserName, setCurrentUserName] = useState<string>('')
  const canManageLists = isAdmin() || isProduction()

  const dataProductOptions = [
    { label: 'METAR Weather', value: 'metar' },
    { label: 'Marine Conditions', value: 'marine' },
    { label: 'Trail Conditions', value: 'trail' },
    { label: 'NWS Observations', value: 'nws' },
    { label: 'HF Radio Propagation', value: 'hf_propagation' },
    { label: 'Beach Conditions', value: 'beach_conditions' }
  ]


  useEffect(() => {
    const init = async () => {
      // Get current user's sub for scoping
      try {
        const session = await fetchAuthSession()
        const payload = session.tokens?.idToken?.payload as any
        if (payload?.sub) {
          setCurrentUserId(payload.sub)
          const givenName = payload.given_name || ''
          const familyName = payload.family_name || ''
          const fullName = [givenName, familyName].filter(Boolean).join(' ')
          setCurrentUserName(fullName || payload.email?.split('@')[0] || 'You')
        }
      } catch (error) {
        console.error('Error getting current user:', error)
      }
      
      loadLocationLists()
      loadDevices()
    }
    init()
  }, [])



  const loadLocationLists = async () => {
    setLoading(true)
    try {
      if (canManageLists) {
        // Admin/Production: load all users' location lists
        const usersResponse = await apiService.getUsers()
        
        if (usersResponse.statusCode === 200 && usersResponse.data) {
          let users: any[] = []
          const responseData: any = usersResponse.data
          
          if (responseData.users && Array.isArray(responseData.users)) {
            users = responseData.users
          } else if (Array.isArray(responseData)) {
            users = responseData
          }
          
          const filteredUsers = filterUserId 
            ? users.filter((u: any) => (u.user_id || u.userId || u.id) === filterUserId)
            : users
          
          const allLocationLists: LocationList[] = []

          for (const user of filteredUsers) {
            const userId = user.user_id || user.userId || user.id
            const givenName = user.given_name || user.givenName || '';
            const familyName = user.family_name || user.familyName || '';
            const fullName = [givenName, familyName].filter(Boolean).join(' ');
            const username = fullName || user.email || 'Unknown User'

            // First get summary to find which data products have lists
            try {
              const summaryResponse = await apiService.getUserLocationLists(userId)
              if (summaryResponse.statusCode !== 200 || !summaryResponse.data) continue
              
              const summaryData = summaryResponse.data as any
              const listsWithData = (summaryData.location_lists || [])
                .filter((ls: any) => ls.has_list)
                .map((ls: any) => ls.data_product)
              
              // Only fetch detailed data for products that actually have lists
              for (const dataProductValue of listsWithData) {
                try {
                  const listResponse = await apiService.getUserLocationList(userId, dataProductValue)
                  
                  if (listResponse.statusCode === 200 && listResponse.data) {
                    const listData = listResponse.data
                    allLocationLists.push({
                      id: `${userId}-${dataProductValue}`,
                      userId,
                      username,
                      dataProduct: dataProductValue,
                      locationCount: listData.locations?.length || 0,
                      maxLocations: listData.max_locations || 50,
                      lastUpdated: formatLastUpdated(listData.updated_at || listData.updatedAt),
                      locations: (listData.locations || []).map((loc: any) => ({
                        locationId: loc.location_id || loc.locationId,
                        name: loc.commonName || loc.common_name || loc.name || loc.location_id || loc.locationId,
                        order: loc.order || 0,
                        coordinates: loc.coordinates
                      }))
                    })
                  }
                } catch (error) {
                  console.log(`Error loading location list for user ${userId} and product ${dataProductValue}`)
                }
              }
            } catch (error) {
              console.log(`Error loading location lists summary for user ${userId}`)
            }
          }

          setLocationLists(allLocationLists)
        } else if (usersResponse.statusCode === 502) {
          throw new Error('Server error - Lambda function may be experiencing issues')
        } else {
          throw new Error(usersResponse.error || 'Failed to load users')
        }
      } else {
        // Standard user: load only their own location lists
        if (!currentUserId) {
          // Wait for currentUserId to be set
          try {
            const session = await fetchAuthSession()
            const payload = session.tokens?.idToken?.payload as any
            if (!payload?.sub) throw new Error('Could not determine user ID')
            const userId = payload.sub
            const givenName = payload.given_name || ''
            const familyName = payload.family_name || ''
            const fullName = [givenName, familyName].filter(Boolean).join(' ')
            const username = fullName || payload.email?.split('@')[0] || 'You'
            
            setCurrentUserId(userId)
            setCurrentUserName(username)
            
            await loadUserOwnLists(userId, username)
          } catch (error) {
            console.error('Error getting current user for location lists:', error)
            setAlert({
              type: 'error',
              message: 'Failed to load your location lists.'
            })
            setLocationLists([])
          }
        } else {
          await loadUserOwnLists(currentUserId, currentUserName)
        }
      }
    } catch (error) {
      console.error('Error loading location lists:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load location lists. Please try again.'
      })
      setLocationLists([])
    } finally {
      setLoading(false)
    }
  }

  const loadDevices = async () => {
    try {
      const response = await apiService.getDevices()
      if (response.statusCode === 200 && response.data) {
        const responseData = response.data as any
        setDevices(responseData.devices || responseData || [])
      }
    } catch (error) {
      console.error('Error loading devices:', error)
    }
  }

  const loadUserOwnLists = async (userId: string, username: string) => {
    const allLocationLists: LocationList[] = []
    
    // First get summary to find which data products have lists
    try {
      const summaryResponse = await apiService.getUserLocationLists(userId)
      if (summaryResponse.statusCode !== 200 || !summaryResponse.data) {
        setLocationLists([])
        return
      }
      
      const summaryData = summaryResponse.data as any
      const listsWithData = (summaryData.location_lists || [])
        .filter((ls: any) => ls.has_list)
        .map((ls: any) => ls.data_product)
      
      // Only fetch detailed data for products that actually have lists
      for (const dataProductValue of listsWithData) {
        try {
          const listResponse = await apiService.getUserLocationList(userId, dataProductValue)
          
          if (listResponse.statusCode === 200 && listResponse.data) {
            const listData = listResponse.data
            allLocationLists.push({
              id: `${userId}-${dataProductValue}`,
              userId,
              username,
              dataProduct: dataProductValue,
              locationCount: listData.locations?.length || 0,
              maxLocations: listData.max_locations || 50,
              lastUpdated: formatLastUpdated(listData.updated_at || listData.updatedAt),
              locations: (listData.locations || []).map((loc: any) => ({
                locationId: loc.location_id || loc.locationId,
                name: loc.commonName || loc.common_name || loc.name || loc.location_id || loc.locationId,
                order: loc.order || 0,
                coordinates: loc.coordinates
              }))
            })
          }
        } catch (error) {
          console.log(`Error loading location list for product ${dataProductValue}`)
        }
      }
    } catch (error) {
      console.log('Error loading location lists summary')
    }
    
    setLocationLists(allLocationLists)
  }

  const formatLastUpdated = (dateString: string): string => {
    if (!dateString) return 'Never'
    
    try {
      const date = new Date(dateString)
      const now = new Date()
      const diffMinutes = (now.getTime() - date.getTime()) / (1000 * 60)
      
      if (diffMinutes < 1) return 'Just now'
      if (diffMinutes < 60) return `${Math.floor(diffMinutes)} minutes ago`
      if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} hours ago`
      return `${Math.floor(diffMinutes / 1440)} days ago`
    } catch {
      return dateString
    }
  }

  // Default reference location for HF Propagation: Wheaton, IL
  const DEFAULT_HF_REFERENCE_LOCATION = { latitude: 41.8661, longitude: -88.1070 }

  // Filter out data products that the current user already has lists for
  const availableDataProductOptions = dataProductOptions.filter(
    opt => !locationLists.some(list => list.userId === currentUserId && list.dataProduct === opt.value)
  )

  const handleCreateList = async () => {
    try {
      const payload: any = {
        data_product: newList.dataProduct,
        location_ids: [],
        max_locations: 50
      }

      // HF Propagation lists use a reference location instead of individual locations
      if (newList.dataProduct === 'hf_propagation') {
        payload.reference_location = DEFAULT_HF_REFERENCE_LOCATION
      }

      const response = await apiService.createLocationList(currentUserId, payload)
      
      if (response.statusCode === 200 || response.statusCode === 201) {
        await loadLocationLists() // Reload location lists
        setShowCreateModal(false)
        const createdProduct = newList.dataProduct
        setNewList({ dataProduct: '' })
        setAlert({
          type: 'success',
          message: createdProduct === 'hf_propagation'
            ? 'HF Propagation list created with default locations'
            : 'Location list created successfully'
        })
      } else {
        const errorData = response.data as any
        const errorMsg = errorData?.error || response.error || 'Failed to create location list'
        throw new Error(errorMsg)
      }
    } catch (error) {
      console.error('Error creating location list:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to create location list. Please try again.'
      })
    }
  }

  const handleEditList = async (list: LocationList) => {
    setEditingList(list)
    setShowEditModal(true)
    setSearchQuery('') // Reset search when opening modal
    
    // Don't load all locations initially - wait for user to search
    setAvailableLocations([])
    
    // Set currently selected locations
    const selected = list.locations.map(loc => ({
      label: list.dataProduct === 'hf_propagation' && loc.coordinates
        ? `${loc.name} (${loc.coordinates.latitude}, ${loc.coordinates.longitude})`
        : loc.name,
      value: loc.locationId
    }))
    setSelectedLocations(selected)
  }

  const loadAvailableLocations = async (dataProduct: string, search?: string) => {
    setLoadingLocations(true)
    try {
      const response = await apiService.getLocations(dataProduct, search)
      
      if (response.statusCode === 200 && response.data) {
        // Handle the response format: { status: "success", locations: [...] }
        let locations: any[] = []
        const responseData: any = response.data
        
        if (responseData.locations && Array.isArray(responseData.locations)) {
          locations = responseData.locations
        } else if (Array.isArray(responseData)) {
          locations = responseData
        }
        
        const options = locations.map((loc: any) => ({
          label: loc.commonName || loc.common_name || loc.name || loc.location_id || loc.locationId,
          value: loc.location_id || loc.locationId
        }))
        setAvailableLocations(options)
      }
    } catch (error) {
      console.error('Error loading available locations:', error)
      setAvailableLocations([])
    } finally {
      setLoadingLocations(false)
    }
  }

  // Debounced search function
  useEffect(() => {
    if (!editingList) return
    
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        // Only search if user typed at least 2 characters
        loadAvailableLocations(editingList.dataProduct, searchQuery)
      } else if (searchQuery.trim().length === 0) {
        // Clear results when search is empty
        setAvailableLocations([])
      }
    }, 500) // 500ms debounce
    
    return () => clearTimeout(timeoutId)
  }, [searchQuery, editingList])

  const handleSaveLocations = async () => {
    if (!editingList) return
    
    try {
      const locationIds = selectedLocations.map(loc => loc.value)
      
      const response = await apiService.updateLocationList(
        editingList.userId,
        editingList.dataProduct,
        { location_ids: locationIds }
      )
      
      if (response.statusCode === 200) {
        await loadLocationLists()
        setShowEditModal(false)
        setEditingList(null)
        setSelectedLocations([])
        setAlert({
          type: 'success',
          message: 'Location list updated successfully'
        })
      } else {
        throw new Error(response.error || 'Failed to update location list')
      }
    } catch (error) {
      console.error('Error updating location list:', error)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update location list. Please try again.'
      })
    }
  }

  const getDataProductBadge = (dataProduct: string) => {
    const product = dataProductOptions.find(p => p.value === dataProduct)
    return <Badge color="blue">{product?.label || dataProduct}</Badge>
  }

  const getUsageBadge = (current: number, max: number) => {
    const percentage = (current / max) * 100
    let color: 'green' | 'blue' | 'red' = 'green'
    
    if (percentage > 80) color = 'red'
    else if (percentage > 60) color = 'blue'
    
    return <Badge color={color}>{current}/{max} locations</Badge>
  }

  const handleCreatePrintMap = (list: LocationList) => {
    // Navigate to Print Map Maker with pre-loaded list ID
    navigate('/print-map-maker', {
      state: {
        preloadedListId: list.id,
        preloadedListData: {
          userId: list.userId,
          dataProduct: list.dataProduct,
          locationCount: list.locationCount,
          locations: list.locations
        }
      }
    })
  }

  const handleDeleteList = async () => {
    if (!deletingList) return
    try {
      const response = await apiService.deleteLocationList(deletingList.userId, deletingList.dataProduct)
      if (response.statusCode === 200) {
        setShowDeleteModal(false)
        setDeletingList(null)
        await loadLocationLists()
        await loadDevices()
        setAlert({ type: 'success', message: 'Location list deleted successfully' })
      } else if (response.statusCode === 409) {
        setShowDeleteModal(false)
        setDeletingList(null)
        setAlert({ type: 'error', message: response.error || 'Cannot delete: list is assigned to a device' })
      } else {
        throw new Error(response.error || 'Failed to delete location list')
      }
    } catch (error) {
      setShowDeleteModal(false)
      setDeletingList(null)
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete location list'
      })
    }
  }

  const handleUnassignDevice = async (deviceId: string) => {
    try {
      const response = await apiService.unassignLocationListFromDevice(deviceId)
      if (response.statusCode === 200) {
        await loadLocationLists()
        await loadDevices()
        setAlert({ type: 'success', message: `Device ${deviceId} unassigned successfully` })
      } else {
        throw new Error(response.error || 'Failed to unassign device')
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to unassign device'
      })
    }
  }

  const getAssignedDevices = (list: LocationList) => {
    return devices.filter(device => {
      const assigned = device.configuration?.assigned_location_list
      return assigned?.user_id === list.userId && assigned?.data_product === list.dataProduct
    })
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={loadLocationLists} loading={loading}>
              Refresh
            </Button>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Create Location List
            </Button>
          </SpaceBetween>
        }
        description={canManageLists 
          ? (filterUserId ? `Showing location lists for user: ${filterUserId}` : 'Showing all location lists')
          : 'Your location lists'
        }
      >
        {canManageLists ? 'Location Lists' : 'My Location Lists'}
      </Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Container>
        <Cards
          cardDefinition={{
            header: item => `${item.username} - ${dataProductOptions.find(p => p.value === item.dataProduct)?.label}`,
            sections: [
              {
                id: 'dataProduct',
                header: 'Data Product',
                content: item => getDataProductBadge(item.dataProduct)
              },
              {
                id: 'usage',
                header: 'Usage',
                content: item => getUsageBadge(item.locationCount, item.maxLocations)
              },
              {
                id: 'lastUpdated',
                header: 'Last Updated',
                content: item => item.lastUpdated
              },
              {
                id: 'preview',
                header: 'Top Locations',
                content: item => (
                  <Box>
                    {item.locations.slice(0, 3).map((location, index) => (
                      <div key={location.locationId} style={{ marginBottom: '4px' }}>
                        <Badge color="grey">{index + 1}</Badge> {location.name}
                      </div>
                    ))}
                    {item.locationCount > 3 && (
                      <div style={{ color: '#666', fontSize: '0.9em' }}>
                        ...and {item.locationCount - 3} more
                      </div>
                    )}
                    {item.locationCount === 0 && (
                      <div style={{ color: '#666', fontSize: '0.9em' }}>
                        No locations added yet
                      </div>
                    )}
                  </Box>
                )
              },
              {
                id: 'devices',
                header: 'Assigned Devices',
                content: item => {
                  const assignedDevices = getAssignedDevices(item)
                  if (assignedDevices.length === 0) {
                    return <Box color="text-status-inactive">No devices assigned</Box>
                  }
                  return (
                    <SpaceBetween direction="vertical" size="xs">
                      {assignedDevices.map((device: any) => (
                        <div key={device.device_id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Badge color="blue">{device.device_id}</Badge>
                          <Button
                            variant="normal"
                            onClick={() => handleUnassignDevice(device.device_id)}
                          >
                            Unassign
                          </Button>
                        </div>
                      ))}
                    </SpaceBetween>
                  )
                }
              },
              {
                id: 'actions',
                header: 'Actions',
                content: item => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button onClick={() => handleEditList(item)}>
                      Edit Locations
                    </Button>
                    {item.locationCount > 0 && (
                      <Button 
                        variant="primary"
                        onClick={() => handleCreatePrintMap(item)}
                      >
                        Create Print Map
                      </Button>
                    )}
                    {item.locationCount > 0 && (
                      <Button
                        onClick={() => navigate(`/location-lists/preview/${item.userId}/${item.dataProduct}`)}
                      >
                        Preview Map
                      </Button>
                    )}
                    {item.userId !== 'SYSTEM' && (
                      <Button
                        variant="normal"
                        onClick={() => {
                          setDeletingList(item)
                          setShowDeleteModal(true)
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </SpaceBetween>
                )
              }
            ]
          }}
          cardsPerRow={[
            { cards: 1 },
            { minWidth: 600, cards: 2 },
            { minWidth: 900, cards: 3 }
          ]}
          items={locationLists}
          loading={loading}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No location lists</b>
              <Box variant="p" color="inherit">
                No location lists have been created yet.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${locationLists.length})`}
              description="Manage user location lists for different data products"
            >
              Location Lists
            </Header>
          }
        />
      </Container>

      <Modal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        header="Create Location List"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleCreateList}
                disabled={!currentUserId || !newList.dataProduct}
              >
                Create List
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween direction="vertical" size="l">
            <FormField label="Owner">
              <Box>{currentUserName || 'Loading...'}</Box>
            </FormField>
            <FormField label="Data Product" description="Select the data product for this location list">
              <Select
                selectedOption={availableDataProductOptions.find(opt => opt.value === newList.dataProduct) || null}
                onChange={({ detail }) =>
                  setNewList({ ...newList, dataProduct: detail.selectedOption?.value || '' })
                }
                options={availableDataProductOptions}
                placeholder={availableDataProductOptions.length > 0 ? "Select a data product" : "All data products already have lists"}
                disabled={availableDataProductOptions.length === 0}
              />
            </FormField>
            {newList.dataProduct === 'hf_propagation' && (
              <Alert type="info">
                HF Propagation lists use a reference location (default: Wheaton, IL) to calculate path conditions. Target locations can be any lat/long coordinate and are added after creation.
              </Alert>
            )}
          </SpaceBetween>
        </Form>
      </Modal>

      <Modal
        visible={showEditModal}
        onDismiss={() => {
          setShowEditModal(false)
          setEditingList(null)
          setSelectedLocations([])
          setSearchQuery('')
          setAvailableLocations([])
        }}
        header={`Edit Locations - ${editingList?.username} (${dataProductOptions.find(p => p.value === editingList?.dataProduct)?.label})`}
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                variant="link" 
                onClick={() => {
                  setShowEditModal(false)
                  setEditingList(null)
                  setSelectedLocations([])
                  setSearchQuery('')
                  setAvailableLocations([])
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSaveLocations}
                disabled={selectedLocations.length > (editingList?.maxLocations || 50)}
              >
                Save Changes
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween direction="vertical" size="l">
            {editingList?.dataProduct === 'hf_propagation' ? (
              <>
                <Alert type="info">
                  HF Propagation locations are pre-populated from system defaults. Use the remove buttons below to customize your list.
                </Alert>
                {selectedLocations.length > 0 ? (
                  <SpaceBetween direction="vertical" size="xs">
                    {selectedLocations.map((loc, index) => (
                      <div key={loc.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                        <Badge color="grey">{index + 1}</Badge>
                        <span style={{ flex: 1 }}>{loc.label}</span>
                        <Button
                          variant="normal"
                          onClick={() => {
                            const updated = selectedLocations.filter(s => s.value !== loc.value)
                            setSelectedLocations(updated)
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </SpaceBetween>
                ) : (
                  <Box color="text-status-inactive">
                    All locations have been removed.
                  </Box>
                )}
              </>
            ) : (
              <>
                <FormField 
                  label="Search Locations" 
                  description="Type at least 2 characters to search for locations (e.g., KGRB for Green Bay airport)"
                >
                  <Input
                    value={searchQuery}
                    onChange={({ detail }) => setSearchQuery(detail.value)}
                    placeholder="Search by location code..."
                    type="search"
                  />
                </FormField>
                
                {loadingLocations && (
                  <Box textAlign="center">
                    <StatusIndicator type="loading">Searching...</StatusIndicator>
                  </Box>
                )}
                
                {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
                  <Alert type="info">
                    Type at least 2 characters to search
                  </Alert>
                )}
                
                {availableLocations.length > 0 && (
                  <FormField 
                    label="Available Locations" 
                    description="Click to add locations to your list"
                  >
                    <SpaceBetween direction="vertical" size="xs">
                      {availableLocations.map(loc => {
                        const isSelected = selectedLocations.some(s => s.value === loc.value)
                        return (
                          <Box key={loc.value}>
                            <Button
                              onClick={() => {
                                if (!isSelected && selectedLocations.length < (editingList?.maxLocations || 50)) {
                                  setSelectedLocations([...selectedLocations, loc])
                                }
                              }}
                              disabled={isSelected || selectedLocations.length >= (editingList?.maxLocations || 50)}
                              variant={isSelected ? "normal" : "primary"}
                            >
                              {isSelected ? '✓ Added' : 'Add'} {loc.label}
                            </Button>
                          </Box>
                        )
                      })}
                    </SpaceBetween>
                  </FormField>
                )}
              </>
            )}
            
            {editingList?.dataProduct !== 'hf_propagation' && (
            <FormField 
              label="Selected Locations" 
              description={`${selectedLocations.length} of ${editingList?.maxLocations || 50} locations selected`}
              errorText={selectedLocations.length > (editingList?.maxLocations || 50) ? `Maximum ${editingList?.maxLocations || 50} locations allowed` : undefined}
            >
              {selectedLocations.length > 0 ? (
                <Box>
                  {selectedLocations.map((loc, index) => (
                    <div key={loc.value} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge color="grey">{index + 1}</Badge>
                      <span style={{ flex: 1 }}>{loc.label}</span>
                      <Button
                        variant="inline-icon"
                        iconName="close"
                        onClick={() => {
                          setSelectedLocations(selectedLocations.filter(s => s.value !== loc.value))
                        }}
                      />
                    </div>
                  ))}
                </Box>
              ) : (
                <Box color="text-status-inactive">
                  No locations selected. Search and add locations above.
                </Box>
              )}
            </FormField>
            )}
          </SpaceBetween>
        </Form>
      </Modal>

      <Modal
        visible={showDeleteModal}
        onDismiss={() => {
          setShowDeleteModal(false)
          setDeletingList(null)
        }}
        header="Delete Location List"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => {
                setShowDeleteModal(false)
                setDeletingList(null)
              }}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteList}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Box>
            Are you sure you want to delete the <b>{dataProductOptions.find(p => p.value === deletingList?.dataProduct)?.label}</b> location list for <b>{deletingList?.username}</b>?
          </Box>
          <Alert type="warning">
            This action cannot be undone.
          </Alert>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  )
}

export default LocationLists